import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Link,
} from "@beweegbaas/ui";
import { createFileRoute, Outlet, redirect, useRouter } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useState } from "react";
import { useDateFormatter } from "#/hooks/use-date-formatter";
import { toast } from "@beweegbaas/ui";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import { SubmitButton } from "#/components/submit-button";
import { TabsNavigation } from "#/components/tabs-navigation";
import { LessonStatus } from "#/modules/les.docent-gestuurd/components/les-status";
import {
  annuleerLesFn,
  getTeacherLessonData,
  lesAfrondenFn,
  lesAfrondenPrecheckFn,
  startLes,
} from "#/modules/les.docent-gestuurd/lesson-teacher.functions";
import { lockStudentLesson } from "#/modules/les.leerling-gestuurd/lesson-student.functions";
import { getStartLessonFiltersFromLesEditData } from "#/modules/les.start-flow/filters";
import { getGroepeerTypeTranslation } from "#/modules/util.i18n/enum-mapping";
import { incrementProfileProperty, op } from "#/modules/util.instrumentation/op";
import type { components } from "#/schema";
import { isApiFailure } from "#/modules/util.errors/translate";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/lesson/$lesson/teacher",
)({
  loader: async ({ params, location }) => {
    const result = await getTeacherLessonData({ data: { lesId: params.lesson } });

    if (result.notFound || !result.lesson) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/$school/lessons",
        params: { locale: params.locale, schoolyear: params.schoolyear, school: params.school },
      });
    }

    // Administratieve lessen have no scores tab; portal redirects scores → administration.
    if (result.lesson.isAdministratieLes && location.pathname.includes("/teacher/scores")) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/administration",
        params: {
          locale: params.locale,
          schoolyear: params.schoolyear,
          school: params.school,
          lesson: params.lesson,
        },
      });
    }

    return { lesson: result.lesson, lesEditData: result.lesEditData };
  },
  component: TeacherLessonLayout,
  errorComponent: TeacherLessonError,
});

function TeacherLessonLayout() {
  const { lesson, lesEditData } = Route.useLoaderData();
  const { locale, schoolyear, school, lesson: lessonId } = Route.useParams();
  const router = useRouter();

  const startLesFn = useServerFn(startLes);
  const lockStudentLessonFn = useServerFn(lockStudentLesson);
  const annuleerFn = useServerFn(annuleerLesFn);
  const precheckFn = useServerFn(lesAfrondenPrecheckFn);
  const afrondenFn = useServerFn(lesAfrondenFn);

  const [precheckData, setPrecheckData] = useState<
    components["schemas"]["LesAfrondenPrecheckResponse"] | null
  >(null);
  const [showWarning, setShowWarning] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const formatter = useDateFormatter({ month: "long", weekday: "long", day: "numeric" });

  async function handleStart() {
    setIsBusy(true);
    try {
      await startLesFn({ data: { lesId: lessonId } });
      await router.invalidate({ sync: true });
      toast.success("Les gestart");
      void op.track("lesson_started", { lesson_id: lessonId, source: "planned" });
    } finally {
      setIsBusy(false);
    }
  }

  async function handleAnnuleer() {
    setIsBusy(true);
    try {
      await annuleerFn({ data: { lesId: lessonId } });
      await router.invalidate({ sync: true });
      toast.success("Les geannuleerd");
    } finally {
      setIsBusy(false);
    }
  }

  async function finishLesson() {
    await afrondenFn({ data: { lesId: lessonId } });
    await router.invalidate({ sync: true });
    toast.success("Les afgerond");
    void op.track("lesson_completed", { lesson_id: lessonId });
    incrementProfileProperty("lessons_completed");
  }

  async function handleAfronden() {
    setIsBusy(true);
    try {
      const result = await precheckFn({ data: { lesId: lessonId } });
      if (!result || isApiFailure(result)) return;
      if (result.hasUnfinishedResultaten) {
        setPrecheckData(result);
        setShowWarning(true);
        return;
      }
      await finishLesson();
    } finally {
      setIsBusy(false);
    }
  }

  const tabParams = { locale, schoolyear, school, lesson: lessonId };

  return (
    <>
      {precheckData?.hasUnfinishedResultaten && (
        <AlertDialog open={showWarning} onOpenChange={setShowWarning}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{m["common.attention"]()}</AlertDialogTitle>
              <AlertDialogDescription>
                {m["les.actief.afronden_warning.confirm_message"]()}
              </AlertDialogDescription>
            </AlertDialogHeader>

            <ul
              className="bg-muted text-muted-foreground mt-3 flex flex-col
                gap-1 divide-y divide-black/5 rounded-md p-2 text-sm"
            >
              {precheckData.beweegactiviteiten.map((activity) => (
                <li
                  key={activity.beweegactiviteitId}
                  className="flex flex-wrap items-baseline justify-between
                      gap-3 not-first:pt-1 not-last:pb-1"
                >
                  {activity.name}{" "}
                  <span className="shrink-0">
                    (
                    {m["les.actief.afronden_warning.beweegactiviteiten.results_count"]({
                      count: activity.leerlingenWithoutResultaat.length,
                    })}
                    )
                  </span>
                </li>
              ))}
              {precheckData.sociaalEmotioneelThemas.map((theme) => (
                <li
                  key={theme.sociaalEmotioneelThemaId}
                  className="flex flex-wrap items-baseline justify-between
                      gap-3 not-first:pt-1 not-last:pb-1"
                >
                  {theme.name}{" "}
                  <span className="shrink-0">
                    (
                    {m["les.actief.afronden_warning.sociaal.results_count"]({
                      count: theme.leerlingenWithoutResultaat.length,
                    })}
                    )
                  </span>
                </li>
              ))}
            </ul>

            <AlertDialogFooter>
              <AlertDialogCancel variant="outline" size="default">
                {m["common.actions.cancel"]()}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => {
                  setShowWarning(false);
                  await finishLesson();
                }}
              >
                {m["les.actief.toch_afronden"]()}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      <Page>
        <Page.Header className="justify-between text-sm">
          <div className="flex flex-col items-start gap-x-4 2xl:flex-row 2xl:items-center">
            <Page.Title className="flex items-center gap-2 text-2xl font-black">
              {lesson.klasName}
              <LessonStatus status={lesson.status} />
            </Page.Title>
            <div className="text-muted-foreground flex flex-wrap items-center gap-x-2">
              {m["les.actief.groepen_count"]({
                aantalGroepen: lesson.aantalGroepen,
              })}
              {lesson.aantalAanwezig != null && (
                <>
                  <span className="opacity-50">/</span>
                  {m["les.actief.aanwezig_count"]({
                    aanwezig: lesson.aantalAanwezig,
                    totaal: lesson.aantalLeerlingen,
                  })}
                </>
              )}
              <span className="opacity-50">/</span>
              {!lesson.isAdministratieLes && (
                <>
                  {m["les.actief.gegroepeerd"]({
                    groepeerType: getGroepeerTypeTranslation(lesson.groepeerType),
                  })}
                </>
              )}
              {lesson.isAdministratieLes && <>{m["les.start.administration_lesson"]()}</>}
              <span className="opacity-50">/</span>
              {formatter.format(new Date(lesson.date))}
              {lesson.canBeEdited && lesEditData && (
                <>
                  <span className="opacity-50">/</span>
                  <Link
                    variant="secondary"
                    underline={true}
                    to="/$locale/{-$schoolyear}/$school/start/les-flow/2"
                    params={{ locale, schoolyear, school }}
                    search={getStartLessonFiltersFromLesEditData(lesEditData, lessonId)}
                  >
                    {m["les.docent.bewerken"]()}
                  </Link>
                </>
              )}
            </div>
          </div>
          {(lesson.status === "Actief" || lesson.canBeStarted) && (
            <Page.HeaderActions className="ml-0">
              {lesson.status === "Actief" && (
                <Page.Header.MainAction>
                  <Button
                    size="xl"
                    variant="default"
                    onClick={async () => {
                      // Lock the session via POST BEFORE entering the student view (Set-Cookie
                      // only persists from POST server fns; portal locked in the student loader).
                      // Passes THIS les as the lock target so the guard can redirect to it without
                      // consulting the eventually-consistent activeLes metadata (dex q7hqg0da).
                      await lockStudentLessonFn({ data: { lesson: lessonId, school } });
                      await router.navigate({
                        to: "/$locale/{-$schoolyear}/$school/lesson/$lesson/student",
                        params: { locale, schoolyear, school, lesson: lessonId },
                      });
                    }}
                  >
                    {m["les.docent.student_gestuurd"]()}
                    <IconMaterialSymbolsArrowRightAltRounded />
                  </Button>
                </Page.Header.MainAction>
              )}

              <Page.Header.SecondaryActions>
                {lesson.status === "Actief" && (
                  <div className="flex items-center gap-2">
                    <SubmitButton
                      type="button"
                      variant="secondary"
                      isPending={isBusy}
                      onClick={handleAnnuleer}
                    >
                      {m["les.actief.annuleer"]()}
                    </SubmitButton>
                    <SubmitButton
                      type="button"
                      variant="secondary"
                      isPending={isBusy}
                      onClick={handleAfronden}
                    >
                      {m["les.actief.afronden"]()}
                    </SubmitButton>
                  </div>
                )}
              </Page.Header.SecondaryActions>

              {lesson.canBeStarted && (
                <Page.Header.MainAction>
                  <SubmitButton
                    type="button"
                    variant="default"
                    size="xl"
                    isPending={isBusy}
                    onClick={handleStart}
                  >
                    {m["les.start.cta"]()}
                    <IconMaterialSymbolsArrowRightAltRounded />
                  </SubmitButton>
                </Page.Header.MainAction>
              )}
            </Page.HeaderActions>
          )}
        </Page.Header>

        <Page.Subnavigation>
          <TabsNavigation className="min-w-0 grow">
            <TabsNavigation.List aria-label={m["les.start.beweegactiviteitenreeksen"]()}>
              {!lesson.isAdministratieLes && (
                <TabsNavigation.Link
                  to="/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/scores"
                  params={tabParams}
                >
                  {m["les.docent.tabs.scores"]()}
                </TabsNavigation.Link>
              )}
              <TabsNavigation.Link
                to="/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/administration"
                params={tabParams}
              >
                {m["les.docent.tabs.administratie"]()}
              </TabsNavigation.Link>

              <TabsNavigation.Link
                to="/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/sport-participation"
                params={tabParams}
              >
                {m["sportdeelname.title"]()}
              </TabsNavigation.Link>

              {!lesson.isAdministratieLes && (
                <TabsNavigation.Link
                  to="/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/mediakaarten"
                  params={tabParams}
                >
                  {m["les.docent.tabs.mediakaarten"]()}
                </TabsNavigation.Link>
              )}

              {!lesson.isAdministratieLes && (
                <TabsNavigation.Link
                  to="/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/les-info"
                  params={tabParams}
                >
                  {m["les.docent.tabs.lesinfo"]()}
                </TabsNavigation.Link>
              )}
              <TabsNavigation.Link
                to="/$locale/{-$schoolyear}/$school/lesson/$lesson/teacher/notities"
                params={tabParams}
              >
                {m["common.labels.notities"]()}
              </TabsNavigation.Link>
            </TabsNavigation.List>
          </TabsNavigation>
        </Page.Subnavigation>

        <Page.Body>
          <Outlet />
        </Page.Body>
      </Page>
    </>
  );
}

function TeacherLessonError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}
